import { Box, Typography } from '@material-ui/core'
import { Stack, Grid } from '@mui/material'
import { HighschoolData } from './useGetHighschoolPreviewData'
import { PropsWithChildren } from 'react'
import PreviewSection from './PreviewSection'
import { useAppSelector } from 'store'
import { TITLE_1_PROGRAM_OPTIONS } from '../title-one'

function percentFormat(num: number) {
  if (num === 0) {
    return '0%' // NOTE: Shouldn't happen based on usage, but just in case.
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  return formatter.format(num)
}

const SchoolDetailsSection = ({ fieldName, children }: PropsWithChildren<{ fieldName: string }>) => {
  return (
    <Stack
      direction="column"
      gap={0.5}
    >
      <Typography
        variant="subtitle2"
        color="textSecondary"
      >
        {fieldName}
      </Typography>
      {children}
    </Stack>
  )
}

const SchoolDetailsValue = ({ fieldName, value }: { fieldName: string; value: string }) => {
  return (
    <SchoolDetailsSection fieldName={fieldName}>
      <Typography
        variant="body1"
        color="textPrimary"
      >
        {value}
      </Typography>
    </SchoolDetailsSection>
  )
}

const SchoolDetailsContainer = ({ highschool }: { highschool: HighschoolData }) => {
  const isPremiumExplorerEnabled = useAppSelector(
    (state) => state.college.record?.Features?.SupportsPremiumHighSchoolExplorer || false,
  )
  if (!highschool) return null
  return (
    <PreviewSection title="School Details">
      <Box sx={{ width: '100%' }}>
        <Grid
          container
          rowGap={3}
        >
          {isPremiumExplorerEnabled && (
            <Grid xs={6}>
              <SchoolDetailsValue
                fieldName="Locale"
                value={highschool?.locale ?? '—'}
              />
            </Grid>
          )}
          <Grid xs={6}>
            <SchoolDetailsValue
              fieldName="Religious Affiliation"
              value={highschool.religiousAffiliation ?? '—'}
            />
          </Grid>
          <Grid xs={6}>
            <SchoolDetailsValue
              fieldName="CEEB Code"
              value={highschool.ceebCode ?? '—'}
            />
          </Grid>
          <Grid xs={6}>
            <SchoolDetailsValue
              fieldName="Title 1 Eligible"
              value={highschool.isTitle1Eligible === undefined ? '—' : highschool.isTitle1Eligible ? 'Yes' : 'No'}
            />
          </Grid>
          {isPremiumExplorerEnabled && (
            <>
              <Grid xs={6}>
                <SchoolDetailsValue
                  fieldName="Title 1 Program"
                  value={highschool.title1Program ? TITLE_1_PROGRAM_OPTIONS?.[highschool.title1Program].label : '—'}
                />
              </Grid>
              <Grid xs={6}>
                <SchoolDetailsValue
                  fieldName="Senior Class Size"
                  value={highschool?.seniorClassSize?.toString() ?? '—'}
                />
              </Grid>
              <Grid xs={6}>
                <SchoolDetailsValue
                  fieldName="% Free or Reduced lunch"
                  value={percentFormat(highschool?.pctFreeReducedLunch ?? 0)}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </PreviewSection>
  )
}

export default SchoolDetailsContainer
